import {
  API_CROWLINGO,
  getWorkspaceId,
  getServerWorkspaceId,
  subscribeWorkspaceId,
} from "@/api/core";
import { AccessToken } from "@/models/account";
import { useAccess } from "@/utils/access-provider";
import { emitSessionExpired } from "@/utils/session-expired";
import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import type { UseQueryOptions } from "@tanstack/react-query";
import { AxiosError, AxiosRequestConfig } from "axios";
import logger from "@/utils/logger";
import { useMemo, useSyncExternalStore } from "react";

type config = AxiosRequestConfig & { url: string; enabled?: boolean };

const getRefreshToken = async (token: string) => {
  return await API_CROWLINGO.request<AccessToken>({
    url: "users/refresh/",
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
};

/**
 * Shared token refresh mechanism.
 * When multiple queries receive 401 simultaneously, only ONE refresh request
 * is sent. All others wait for the same promise and reuse the new token.
 * This prevents race conditions where concurrent refreshes cause cascading
 * re-renders and potential logout on pages with many parallel queries
 * (e.g. /trackers?id=... which fires 5+ queries at once).
 */
let refreshPromise: Promise<string> | null = null;

const refreshTokenShared = async (refreshToken: string): Promise<string> => {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = getRefreshToken(refreshToken)
    .then((response) => response.data.access_token)
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
};

const applyRequest = async <Data>(
  axiosConfig: config,
  accessToken?: string,
  data?: any,
) => {
  try {
    let url = axiosConfig.url;

    // replace url params between {} with the value in uriParams from data
    let { uriParams, ...body } = data ?? { ...axiosConfig.data };
    uriParams &&
      Object.keys(uriParams).forEach((key) => {
        url = url.replace(`{${key}}`, uriParams[key]);
      });

    if (body && Object.keys(body).length === 1 && body._) {
      body = body._;
    }

    return await API_CROWLINGO.request<Data>({
      ...axiosConfig,
      url,
      headers: {
        ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
        ...axiosConfig.headers,
      },
      data: body,
    });
  } catch (error: any) {
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }

    throw error;
  }
};

export const requestByToken = async <Data>(
  accessToken: AccessToken,
  axiosConfig: config,
  data: { [key: string]: any } | undefined,
  maxRetry = 2,
): Promise<Data> => {
  if (maxRetry === 0) {
    emitSessionExpired();
    throw new Error("Max retry reached");
  }

  try {
    const response = await applyRequest(
      axiosConfig,
      accessToken.access_token,
      data,
    );

    return response.data as Data;
  } catch (error: any) {
    if (error?.response?.status === 401) {
      const newToken = await refreshTokenShared(accessToken.refresh_token);
      const newAccessToken = {
        ...accessToken,
        access_token: newToken,
      };

      return requestByToken(newAccessToken, axiosConfig, data, maxRetry - 1);
    }

    throw error;
  }
};

type ApiQueryConfig<Data> = Omit<
  UseQueryOptions<Data, AxiosError, Data, string[]>,
  "queryKey" | "queryFn"
>;

export const useApiQuery = <Data>(
  axiosConfig: config,
  queryConfig?: ApiQueryConfig<Data>,
) => {
  const { accessToken, setAccessToken } = useAccess();

  const { enabled: configEnabled, ...restAxiosConfig } = axiosConfig;
  const isEnabled = queryConfig?.enabled ?? configEnabled;

  // Reactive workspace ID via useSyncExternalStore — one shared subscription
  // for all useApiQuery consumers, batched into a single re-render pass.
  const workspaceId = useSyncExternalStore(
    subscribeWorkspaceId,
    getWorkspaceId,
    getServerWorkspaceId,
  );

  // Stable query key: includes workspace ID so each workspace has its own
  // cache entry. Auth token is intentionally excluded — it's handled inside
  // the queryFn via the refresh interceptor.
  const queryKey = useMemo((): string[] => {
    try {
      const configString = JSON.stringify({
        url: restAxiosConfig.url,
        method: restAxiosConfig.method,
        params: restAxiosConfig.params,
        data: restAxiosConfig.data,
        _ws: workspaceId,
      });
      return [configString];
    } catch {
      return [String(restAxiosConfig)];
    }
  }, [restAxiosConfig, workspaceId]);

  return useQuery<Data, AxiosError, Data, string[]>({
    // Keep previous workspace's data visible while the new workspace loads,
    // preventing a white flash when switching to an unvisited workspace.
    placeholderData: keepPreviousData,
    ...queryConfig,
    queryKey,
    queryFn: async () => {
      try {
        const response = await applyRequest<Data>(
          restAxiosConfig as config,
          accessToken?.access_token,
        );

        return response.data;
      } catch (error: any) {
        if (error?.response?.status === 401) {
          if (!accessToken?.refresh_token) {
            emitSessionExpired();
            throw error;
          }

          try {
            const newAccessToken = await refreshTokenShared(
              accessToken.refresh_token,
            );
            setAccessToken({
              ...accessToken,
              access_token: newAccessToken,
            });

            const response = await applyRequest<Data>(
              restAxiosConfig as config,
              newAccessToken,
            );
            return response.data;
          } catch (refreshError: any) {
            logger.error("Token refresh failed:", refreshError);
            if (refreshError?.response?.status === 401) {
              emitSessionExpired();
            }
            throw refreshError;
          }
        }

        throw error;
      }
    },
    enabled: isEnabled,
    retry:
      queryConfig?.retry ??
      ((failureCount, error) => {
        if (!error?.response) return false;
        return (
          ![401, 404, 500].includes(error.response.status) && failureCount < 3
        );
      }),
  });
};

/**
 * Hook that provides a simple API client with authentication.
 * Useful for making ad-hoc API calls outside of the standard query/mutation patterns.
 */
export const useApi = () => {
  const { accessToken, setAccessToken } = useAccess();

  const request = async <Data>(
    method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE",
    url: string,
    data?: any,
  ): Promise<Data> => {
    try {
      const response = await applyRequest<Data>(
        { url, method },
        accessToken?.access_token,
        data,
      );
      return response.data;
    } catch (error: any) {
      if (error?.response?.status === 401) {
        if (!accessToken?.refresh_token) {
          emitSessionExpired();
          throw error;
        }

        try {
          const newAccessToken = await refreshTokenShared(
            accessToken.refresh_token,
          );
          setAccessToken({
            ...accessToken,
            access_token: newAccessToken,
          });

          const response = await applyRequest<Data>(
            { url, method },
            newAccessToken,
            data,
          );
          return response.data;
        } catch (refreshError: any) {
          if (refreshError?.response?.status === 401) {
            emitSessionExpired();
          }
          throw refreshError;
        }
      }
      throw error;
    }
  };

  return {
    get: <Data>(url: string) => request<Data>("GET", url),
    post: <Data>(url: string, data?: any) => request<Data>("POST", url, data),
    put: <Data>(url: string, data?: any) => request<Data>("PUT", url, data),
    patch: <Data>(url: string, data?: any) => request<Data>("PATCH", url, data),
    delete: <Data>(url: string) => request<Data>("DELETE", url),
  };
};

export const useApiMutation = <Data, Payload, UriParams = undefined>(
  axiosConfig: config,
) => {
  const { accessToken, setAccessToken } = useAccess();

  // Note: Access token is passed to applyRequest which handles auth headers

  type payloadType = UriParams extends {}
    ? Payload & { uriParams: UriParams }
    : Payload;
  const result = useMutation<Data, AxiosError, payloadType>({
    mutationFn: async (data) => {
      try {
        const response = await applyRequest<Data>(
          axiosConfig,
          accessToken?.access_token,
          data,
        );

        return response.data;
      } catch (error: any) {
        if (error?.response?.status === 401) {
          if (!accessToken?.refresh_token) {
            emitSessionExpired();
            throw error;
          }

          try {
            const newAccessToken = await refreshTokenShared(
              accessToken.refresh_token,
            );
            setAccessToken({
              ...accessToken,
              access_token: newAccessToken,
            });

            const response = await applyRequest<Data>(
              axiosConfig,
              newAccessToken,
              data,
            );
            return response.data;
          } catch (refreshError: any) {
            if (refreshError?.response?.status === 401) {
              emitSessionExpired();
            }
            throw refreshError;
          }
        }

        throw error;
      }
    },
    retry: (failureCount, error) => {
      if (!error?.response) return false;
      return (
        ![401, 404, 500].includes(error.response.status) && failureCount < 3
      );
    },
  });

  return result;
};
